import { BillingBudgetCard } from '@/components/features/organisations/BillingBudgetCard'
import { BillingCreditPlanPicker } from '@/components/features/organisations/BillingCreditPlanPicker'
import { BillingCreditsCard } from '@/components/features/organisations/BillingCreditsCard'
import { BillingInvoicesCard } from '@/components/features/organisations/BillingInvoicesCard'
import {
  BillingPaymentMethodCard,
  CheckoutReturnBanner,
  PaymentReturnBanner,
  PaymentReturnStatus,
} from '@/components/features/organisations/BillingPaymentMethodCard'
import {
  BillingPlanCard,
  billingActionFailure,
} from '@/components/features/organisations/BillingPlanCard'
import { BillingStorageCard } from '@/components/features/organisations/BillingStorageCard'
import {
  BillingUpgradeProcessingScreen,
  BillingUpgradeTakingLongerNotice,
} from '@/components/features/organisations/BillingUpgradeProcessing'
import { BillingWalletCard } from '@/components/features/organisations/BillingWalletCard'
import { useUpgradeSettlementPoll } from '@/components/features/organisations/OrgFrozenScreen'
import { useDeclareBillingTakeover } from '@/context/BillingTakeoverContext'
import { useClient } from '@/context/ClientContext'
import { useProUpgradeCheckout } from '@/hooks/useProUpgradeCheckout'
import { showsPaymentMethodCard } from '@/lib/billing-payment-card'
import {
  isCheckoutReturnConfirmed,
  rememberCheckoutReturnOrg,
  takeCheckoutHadPaymentMethod,
} from '@/lib/checkout-return'
import {
  forgetCreditPlanDecline,
  forgetCreditPlanIntent,
  readCreditPlanDecline,
  readCreditPlanIntent,
  rememberCreditPlanDecline,
  rememberCreditPlanIntent,
} from '@/lib/credit-plan-intent'
import {
  PAYMENT_CONFIRM_POLL_MS,
  PAYMENT_CONFIRM_TIMEOUT_MS,
  isPaymentConfirmed,
  rememberPaymentReturn,
  takePaymentReturn,
} from '@/lib/payment-return'
import {
  UPGRADE_CHARGE_START_WATCH_MS,
  UPGRADE_SETTLEMENT_WATCH_MS,
  isProUpgradeProcessing,
  isUpgradeSettlementResolved,
  proUpgradeAutoStart,
  shouldContinueProUpgrade,
  takeProUpgradeIntent,
} from '@/lib/pro-upgrade-intent'
import {
  WALLET_TOP_UP_WATCH_MS,
  WalletTopUpWatch,
  hasTopUpLanded,
  topUpWatchFor,
  walletTopUpState,
} from '@/lib/wallet-top-up'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { WalletRechargeRule } from '@droidmap/organisation-service-contract'
import {
  BILLING_PLAN_CATALOGUE,
  CREDIT_PLAN_CATALOGUE,
  SelfServeCreditPlanTier,
  hasPerOrgBundle,
  isAwaitingFirstPlan,
  oneTimeGrantCents,
  requiresSelfServePaymentMethod,
  starterTrialProgress,
  trialCreditUsage,
} from '@droidmap/shared-service-contract'

/** Keeps the org re-reading while a Trial's first fee settles (#3006). */
function CreditPlanSettlementWatch({ orgId }: { orgId: string }) {
  useUpgradeSettlementPoll(orgId)
  return null
}

const CARD_WATCH_MS = 5 * 60_000
const CARD_WATCH_POLL_MS = 15_000

export const Route = createFileRoute('/_authenticated/orgs/$orgId/billing')({
  component: Billing,
  // Stripe-hosted Checkout sends the admin back here with ?checkout=success or
  // ?checkout=cancel (the return URLs are configured Lago-side). Absent on a
  // normal visit; validated so an unexpected value is dropped rather than
  // throwing.
  validateSearch: z.object({
    checkout: z.enum(['success', 'cancel']).optional(),
    // The frozen-org takeover's "Upgrade to DroidMap Pro" CTA hands off here
    // with `?upgrade=start`, meaning "the admin has already asked to upgrade —
    // don't make them ask again" (#2663). Since #2755 it hands off only what it
    // cannot finish itself: a charge against a card on file, whose settling UI
    // is on this page, and every non-actionable case, which wants the plan
    // card's own state — the no-card Checkout redirect now leaves from the
    // takeover. A string enum rather than a boolean to match `checkout` above,
    // and so an unexpected value is dropped rather than coerced into an upgrade
    // nobody requested.
    upgrade: z.enum(['start']).optional(),
  }),
  loader: async ({ context }) => {
    return { crumb: 'Billing', metadata: context.metadata }
  },
})

function Billing() {
  const { organisationClient } = useClient()
  const { orgId } = Route.useParams()
  const { metadata } = Route.useLoaderData()
  const { org } = useLoaderData({ from: '/_authenticated/orgs/$orgId' })
  const search = Route.useSearch()
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()

  /* -------------------------- Payment-action return ------------------------ */

  // Declared ahead of the queries because the billing query polls while a
  // payment is awaiting confirmation; the effects that resolve these live in
  // the "Stripe / payment return" section below.
  //
  // Read-and-clear on mount: a snapshot is present exactly when this load is
  // the return from the hosted payment page (see lib/payment-return.ts).
  const [paymentReturn] = useState(() => takePaymentReturn(orgId))
  const [paymentConfirmed, setPaymentConfirmed] = useState(false)
  const [paymentTimedOut, setPaymentTimedOut] = useState(false)
  const [paymentReturnDismissed, setPaymentReturnDismissed] = useState(false)

  /* ---------------------- Upgrade-settlement watch (#2654) ----------------- */

  // When THIS session raised a Pro-upgrade charge. The upgrade settles on a
  // Lago webhook, so the single refetch the mutation's `onSuccess` triggers
  // fires while the invoice is still `pending` and the page then had no path
  // back to the truth — it sat on an "awaiting payment" state for an upgrade
  // that had already completed until the admin refreshed by hand.
  //
  // Scoped to the session that charged, NOT to `billing.pendingProUpgrade`:
  // an org can sit pending indefinitely (the #2221 sweeper doesn't exist yet),
  // and keying off the flag would make every later visit to the billing page
  // poll for the full window over a charge nobody is waiting on.
  //
  // Both entry points arm it — the auto-continue after a Checkout return
  // (#2196) and the plain Upgrade click on an org that already has a card. The
  // second has the identical defect; fixing only the first leaves half of it.
  const [upgradeChargedAt, setUpgradeChargedAt] = useState<number | null>(null)
  const [upgradeWatchExpired, setUpgradeWatchExpired] = useState(false)
  // The same giving-up latch for the half of the window that watch cannot see:
  // everything before the charge lands (#2762). See the effect that arms it.
  const [chargeStartExpired, setChargeStartExpired] = useState(false)

  /* ----------------------- Early wallet top-up (#2961) --------------------- */

  // An early top-up this session posted, and the balance before it. The credit
  // lands when Lago settles the payment, so the wallet query polls until the
  // balance rises past `baselineCents` or the watch window closes. Usage can
  // only lower the balance, so any rise is the top-up.
  const [topUpWatch, setTopUpWatch] = useState<WalletTopUpWatch | null>(null)
  const [topUpWatchExpired, setTopUpWatchExpired] = useState(false)

  const paymentReturnStatus: PaymentReturnStatus | null =
    !paymentReturn || paymentReturnDismissed
      ? null
      : paymentConfirmed
        ? 'confirmed'
        : paymentTimedOut
          ? 'unconfirmed'
          : 'pending'

  /* ------------------------------- Queries ------------------------------- */

  const billingQuery = useQuery({
    queryKey: ['org-billing', orgId],
    queryFn: () => organisationClient.getOrgBillingState({ orgId }, metadata),
    // The payment lands asynchronously (Lago webhook), so the one fetch that
    // triggers it is always too early to see it. Two watches drive this, and
    // both stop as soon as their outcome is observed or their window closes:
    // a return from the hosted payment page (#2359), and a Pro-upgrade charge
    // raised by this session (#2654). Outside those the page never polls.
    // The callback form, not a value: the upgrade watch's stop condition is a
    // question about this very query's latest result, which a value computed
    // in the component body could only answer one render late.
    refetchInterval: (query) => {
      if (paymentReturnStatus === 'pending') return PAYMENT_CONFIRM_POLL_MS
      if (upgradeChargedAt === null || upgradeWatchExpired) return false
      return isUpgradeSettlementResolved(
        query.state.data,
        query.state.dataUpdatedAt,
        upgradeChargedAt,
      )
        ? false
        : PAYMENT_CONFIRM_POLL_MS
    },
  })
  const billing = billingQuery.data

  const upgradeSettlementResolved =
    upgradeChargedAt !== null &&
    isUpgradeSettlementResolved(
      billing,
      billingQuery.dataUpdatedAt,
      upgradeChargedAt,
    )

  const storageQuery = useQuery({
    queryKey: ['org-storage-status', orgId],
    queryFn: () => organisationClient.getOrgStorageStatus({ orgId }, metadata),
  })

  const invoicesQuery = useQuery({
    queryKey: ['org-invoices', orgId],
    queryFn: () =>
      organisationClient.listOrgBillingInvoices({ orgId }, metadata),
  })

  // Only prepaid orgs have a wallet; skip the fetch entirely for postpaid.
  const billingMode = billing?.billingMode ?? org.billingMode
  const isPrepaid = billingMode === 'prepaid'

  // Starter is the free entry tier: it never incurs a charge, so it has no
  // invoices by definition. Suppress all invoice display for it — the local
  // Lago stub returns canned invoices for every org regardless of tier, and a
  // real starter org likewise has nothing to bill.
  const planTier = billing?.planTier ?? org.planTier
  // A credit plan keeps `plan_tier = 'starter'` until #3005, so Starter's
  // no-invoices, no-card, read-only-budget treatment applies only to its Trial.
  const creditPlanTier = org.creditPlanTier
  const creditPlan = creditPlanTier
    ? CREDIT_PLAN_CATALOGUE[creditPlanTier]
    : null
  const isStarter =
    planTier === 'starter' && (!creditPlanTier || creditPlanTier === 'trial')
  // Starter is a time-boxed trial (#2045); show how far through the 30-day
  // window the org is. `null` for non-Starter orgs and Starter orgs with no
  // stamped trial clock (e.g. test orgs), which hides the bar entirely.
  const trialProgress = isStarter
    ? starterTrialProgress(org.trialStartedAt, org.trialExpiresAt, new Date())
    : null
  const rechargeQuery = useQuery({
    queryKey: ['org-wallet-recharge', orgId],
    queryFn: () => organisationClient.getOrgWalletRecharge({ orgId }, metadata),
    enabled: isPrepaid && creditPlan === null,
    // Polls only while an early top-up from this session is settling; the
    // callback form for the same reason as the billing query's above.
    refetchInterval: (query) =>
      topUpWatch === null ||
      topUpWatchExpired ||
      hasTopUpLanded(query.state.data?.balanceCents, topUpWatch.baselineCents)
        ? false
        : PAYMENT_CONFIRM_POLL_MS,
  })

  const creditUsageQuery = useQuery({
    // The tier is in the key so a conversion landing re-reads the allowance.
    queryKey: ['org-credit-usage', orgId, creditPlanTier],
    queryFn: () => organisationClient.getOrgCreditUsage({ orgId }, metadata),
    enabled: creditPlan !== null,
  })

  // Starter's one-time grant ($5) in cents — non-null only on Starter. Drives
  // the plan card's "Trial Credits" bar and the budget card's Starter overrides
  // below (spend, plan limit).
  const starterGrantCents = oneTimeGrantCents(planTier)
  // Starter trial-grant usage for the plan card's "Trial Credits" bar (#2045):
  // how much of the one-time $5 grant has been spent (grant − balance). `null`
  // for tiers with no fixed one-time grant, which hides the bar. Same balance
  // source as the wallet card headline.
  //
  // The query is preferred over the row because it is read live from Lago per
  // request while the row is a ≤3-min aggregator mirror. That ordering was only
  // *correct* from #2288 on: before it the service returned Lago's frozen
  // `ongoing_balance_cents`, so this line shadowed the aggregator's already-
  // eroded figure with one that never drew down, blanking both cards. Fixed
  // behind the query — do not "fix" it by flipping the `??`, which would only
  // work when the aggregator value happened to have landed.
  const walletBalanceCents =
    rechargeQuery.data?.balanceCents ?? org.creditBalanceCents
  const trialCredits = trialCreditUsage(starterGrantCents, walletBalanceCents)

  /* ------------------------------ Mutations ------------------------------ */

  // A plan change moves the row, the usage and possibly the freeze, so all three
  // are re-read: the org loader is what lets go of a frozen layout.
  const refreshAfterPlanChange = () => {
    queryClient.invalidateQueries({ queryKey: ['org-billing', orgId] })
    queryClient.invalidateQueries({ queryKey: ['org-credit-usage', orgId] })
    router.invalidate({
      filter: (match) => match.routeId === '/_authenticated/orgs/$orgId',
    })
  }
  const changePlanMutation = useMutation({
    mutationFn: ({
      tier,
      afterCheckout,
    }: {
      tier: SelfServeCreditPlanTier
      afterCheckout: boolean
    }) =>
      organisationClient.changeOrgCreditPlan(
        { orgId, creditPlanTier: tier, afterCheckout },
        metadata,
      ),
    // A cancel from an earlier attempt says nothing about this one's outcome.
    onMutate: () => cancelPendingMutation.reset(),
    onSuccess: refreshAfterPlanChange,
    // A failure can still have moved the plan, in which case the server keeps the
    // claim. Only a re-read shows the admin that, and the way to cancel it.
    onError: refreshAfterPlanChange,
  })
  const cancelPendingMutation = useMutation({
    mutationFn: () =>
      organisationClient.cancelPendingOrgCreditPlan({ orgId }, metadata),
    onSuccess: refreshAfterPlanChange,
  })
  const cancelScheduledMutation = useMutation({
    mutationFn: () =>
      organisationClient.cancelScheduledOrgCreditPlanChange(
        { orgId },
        metadata,
      ),
    onSuccess: refreshAfterPlanChange,
  })
  // The server's refusal verbatim when it sent one, as on the plan card.
  const planChangeFailure = changePlanMutation.isError
    ? (billingActionFailure(changePlanMutation.error)?.message ??
      'Couldn’t change your plan. Please try again in a moment.')
    : null
  // An org created after its creator's trial was used has no plan yet (#3196).
  // It chooses one from the picker, and nothing on this page may offer it the
  // ADR-0013 Pro upgrade or wallet. A failed billing read shows the plan card
  // instead, for its error and retry: without the read the picker can't tell
  // whether there's a card, and would send the admin to Checkout regardless.
  const awaitingFirstPlan = isAwaitingFirstPlan(org)
  const choosesFirstPlan = awaitingFirstPlan && !billingQuery.isError
  const showsPlanCard = !awaitingFirstPlan || billingQuery.isError
  // Ops-managed plans aren't the admin's to change, and neither is a plan while
  // an ADR-0013 upgrade is settling.
  const canChangeCreditPlan =
    (choosesFirstPlan ||
      creditPlanTier === 'trial' ||
      creditPlanTier === 'pro' ||
      creditPlanTier === 'max' ||
      creditPlanTier === 'ultra') &&
    !billing?.pendingProUpgrade

  // Lago's stored card changes when Stripe's webhook lands, shortly after a
  // return from Checkout, so watch for the digits to change for a few minutes.
  const [cardWatchUntil] = useState(() =>
    search.checkout === 'success' ? Date.now() + CARD_WATCH_MS : 0,
  )
  const cardQuery = useQuery({
    queryKey: ['org-billing-card', orgId],
    queryFn: () => organisationClient.getOrgBillingCard({ orgId }, metadata),
    enabled: canChangeCreditPlan && (billing?.hasPaymentMethod ?? false),
    refetchInterval: () =>
      Date.now() < cardWatchUntil ? CARD_WATCH_POLL_MS : false,
  })

  // The fee settling means the page has to notice when it lands: nothing else
  // re-reads the org on its own. A claim that clears with the org still on its
  // Trial is one that failed.
  const pendingCreditPlanTier = org.pendingCreditPlanTier ?? null
  const [wasPending, setWasPending] = useState(false)
  // Kept across a reload and a cancelled Checkout, so the warning about retrying
  // the card still shows. A completed Checkout means they've changed it.
  const [conversionFailed, setConversionFailed] = useState(
    () => search.checkout !== 'success' && readCreditPlanDecline(orgId),
  )
  useEffect(() => {
    if (conversionFailed) rememberCreditPlanDecline(orgId)
    else forgetCreditPlanDecline(orgId)
  }, [conversionFailed, orgId])
  // A stored decline can outlast the Trial: the conversion may have completed while
  // this page wasn't mounted, or in another tab. Only a Trial, or an org choosing
  // its first plan, can have one.
  const convertsOnPayment = creditPlanTier === 'trial' || awaitingFirstPlan
  if (conversionFailed && !convertsOnPayment) {
    setConversionFailed(false)
  }
  if (pendingCreditPlanTier && !wasPending) {
    setWasPending(true)
    setConversionFailed(false)
  }
  if (!pendingCreditPlanTier && wasPending) {
    setWasPending(false)
    setConversionFailed(
      convertsOnPayment && cancelPendingMutation.data?.status !== 'cancelled',
    )
  }

  // The plan chosen before leaving for Checkout to add a card. Latched on arrival
  // like `continueProUpgrade`, since the checkout marker is about to be stripped.
  const [creditPlanIntent] = useState(() =>
    search.checkout === 'success' ? readCreditPlanIntent(orgId) : null,
  )
  const creditPlanIntentStarted = useRef(false)
  useEffect(() => {
    if (search.checkout === 'cancel') forgetCreditPlanIntent(orgId)
  }, [search.checkout, orgId])
  useEffect(() => {
    if (!creditPlanIntent || creditPlanIntentStarted.current) return
    if (!canChangeCreditPlan) return
    // The same settled read the Pro continuation waits on. The server re-checks
    // the card, with its own retry for one still landing.
    if (billingQuery.isPending || billingQuery.isFetching) return
    creditPlanIntentStarted.current = true
    forgetCreditPlanIntent(orgId)
    changePlanMutation.mutate({ tier: creditPlanIntent, afterCheckout: true })
    // `mutate` is stable; the mutation object isn't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creditPlanIntent,
    canChangeCreditPlan,
    billingQuery.isPending,
    billingQuery.isFetching,
    orgId,
  ])

  // The outbound Checkout leg — mint the URL, stash what the return needs,
  // leave for Stripe. Lifted into a hook so the frozen-org takeover's CTA can
  // run it too, instead of navigating here just to have this code execute
  // (#2755). Both entry points share it; nothing about the *return* moved.
  const checkout = useProUpgradeCheckout({
    orgId,
    metadata,
    hasPaymentMethod: billing?.hasPaymentMethod ?? false,
  })
  // A Checkout that isn't the picker's must not inherit the picker's choice: it
  // would change the plan and charge the card on return, with no confirmation.
  const { startProUpgradeCheckout: startCheckoutForProUpgrade } = checkout
  const startProUpgradeCheckout = useCallback(() => {
    forgetCreditPlanIntent(orgId)
    startCheckoutForProUpgrade()
  }, [orgId, startCheckoutForProUpgrade])
  useEffect(() => {
    // No redirect happened, so nothing is coming back to act on it.
    if (checkout.isError) forgetCreditPlanIntent(orgId)
  }, [checkout.isError, orgId])

  // Abandon a Pro upgrade stuck awaiting payment (#2222). The service voids the
  // outstanding charge and clears the pending flag — or refuses, answering
  // `payment_succeeded`, when the payment actually landed. Either way the
  // authoritative state is the refetched one, so invalidate both the billing
  // query and the parent org loader: the plan card reads its tier from
  // `billing?.planTier ?? org.planTier`, and a refused cancel is usually
  // followed by the org flipping to Pro.
  const cancelUpgradeMutation = useMutation({
    mutationFn: () => organisationClient.cancelProUpgrade({ orgId }, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-billing', orgId] })
      router.invalidate()
    },
  })

  // Payment-gated Pro upgrade (#2107): charges the up-front payment and marks
  // the org pending. The tier flips to Pro asynchronously on payment success
  // (Lago webhook), so we just refetch the billing state — it now reports
  // `pendingProUpgrade`, which drives the plan card's "awaiting payment" state.
  //
  // `afterCheckout` tells the server this start is the auto-continue firing on
  // a Checkout return rather than a plain Upgrade click. It is observability
  // only (#2535) — the server refuses or charges on its own signals — but it is
  // what makes a refusal issued to someone who *did* just add a card show up as
  // the system fault it is instead of looking like a user who never did.
  const proUpgradeMutation = useMutation({
    mutationFn: ({ afterCheckout }: { afterCheckout: boolean }) =>
      organisationClient.startProUpgrade({ orgId, afterCheckout }, metadata),
    // Starting a fresh upgrade retires whatever the previous cancel answered,
    // so its notice can't reappear over a new pending state (#2222) — and the
    // pre-charge watch's expiry with it, or a retry after that watch gave up
    // would never show the processing state at all (#2762).
    onMutate: () => {
      cancelUpgradeMutation.reset()
      setChargeStartExpired(false)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-billing', orgId] })
      // The org loader too, for the same reason `cancelProUpgrade` does it:
      // `pendingProUpgrade` now rides on the org row as well (#2654), and the
      // frozen-org gate reads it from THERE, not from the billing query. Without
      // this the loader keeps its pre-charge snapshot for the whole settling
      // window, so `upgradeSettling` stays false and the admin who just paid is
      // still shown "Your trial has ended … deleted in N days" — the very copy
      // the in-progress notice exists to replace, on the one path that produces
      // it. The poll that would have corrected it never starts either: it lives
      // inside the notice this flag gates.
      router.invalidate()
      // Open the settlement watch (#2654). This invalidation is the only
      // refetch the charge itself produces, and it resolves while the invoice
      // is still `pending` — from here on the page needs the poll to reach the
      // settled state without the admin refreshing by hand.
      //
      // The expiry latch is cleared with it: a second charge in the same page
      // session (a first attempt abandoned, then retried in place — no
      // redirect, so no remount) opens a NEW watch, and leaving the previous
      // one's expiry standing would short-circuit the poll before it ran once,
      // reinstating this issue verbatim for the retry.
      setUpgradeWatchExpired(false)
      setUpgradeChargedAt(Date.now())
    },
  })
  // `mutate` is a stable reference across renders (the mutation *object* is
  // not), so pulling it out lets the continue-upgrade effect below depend on
  // exactly what it calls without re-running on every render.
  const startProUpgrade = proUpgradeMutation.mutate

  const ceilingMutation = useMutation({
    mutationFn: (storageCeilingBytes: number | null) =>
      organisationClient.updateOrganisation(
        { orgId, storageCeilingBytes },
        metadata,
      ),
    onSuccess: () => {
      // The ceiling tightens the effective cap and lives on the org row.
      queryClient.invalidateQueries({ queryKey: ['org-storage-status', orgId] })
      router.invalidate()
    },
  })

  const budgetMutation = useMutation({
    mutationFn: (input: {
      monthlyBudgetCents: number | null
      budgetEnforce: boolean
    }) => organisationClient.updateOrganisation({ orgId, ...input }, metadata),
    onSuccess: () => {
      // Budget + enforce live on the org row that the parent loader resolves;
      // re-running it refreshes the spend-vs-budget card.
      router.invalidate()
    },
  })

  const rechargeMutation = useMutation({
    mutationFn: (rechargeRule: WalletRechargeRule) =>
      organisationClient.updateOrgWalletRecharge(
        { orgId, rechargeRule },
        metadata,
      ),
    onSuccess: (state) => {
      queryClient.setQueryData(['org-wallet-recharge', orgId], state)
    },
  })

  // Charge the org's recharge amount now (#2961). The server runs the hourly
  // sweep's guards and the same atomic claim, so a double click or a click
  // beside a sweep charges the card at most once; a refusal is a status, not
  // an error.
  const topUpMutation = useMutation({
    mutationFn: async () => {
      // Re-read the balance first: the cached one can predate a credit that
      // landed since, which the watch would then mistake for this top-up.
      const before = await rechargeQuery.refetch()
      const result = await organisationClient.topUpOrgWallet(
        { orgId },
        metadata,
      )
      return { result, baselineCents: before.data?.balanceCents ?? null }
    },
    onMutate: () => {
      setTopUpWatch(null)
      setTopUpWatchExpired(false)
    },
    onSuccess: ({ result, baselineCents }) => {
      const watch = topUpWatchFor(result, baselineCents)
      if (watch === null) return
      setTopUpWatch(watch)
      rechargeQuery.refetch()
    },
  })
  const topUpLanded =
    topUpWatch !== null &&
    hasTopUpLanded(rechargeQuery.data?.balanceCents, topUpWatch.baselineCents)

  // Stop polling a payment that isn't landing rather than poll forever.
  useEffect(() => {
    if (topUpWatch === null || topUpLanded) return
    const timer = setTimeout(
      () => setTopUpWatchExpired(true),
      WALLET_TOP_UP_WATCH_MS,
    )
    return () => clearTimeout(timer)
  }, [topUpWatch, topUpLanded])

  // Once the credit is in, refresh the org row too: the low / exhausted banner
  // and the "as of" stamp come from it, not from the live wallet read.
  useEffect(() => {
    if (topUpLanded) router.invalidate()
  }, [topUpLanded, router])

  const walletTopUp = walletTopUpState({
    isPending: topUpMutation.isPending,
    isError: topUpMutation.isError,
    result: topUpMutation.data?.result,
    watch: topUpWatch,
    landed: topUpLanded,
    watchExpired: topUpWatchExpired,
  })

  /* ------------------------- Payment-action redirect ----------------------- */

  // Leave for the Stripe-hosted page that settles the outstanding invoice —
  // the "Complete payment" CTA offered by both the plan card (up-front Pro
  // upgrade charge) and the payment-method card (3-D Secure step-up).
  //
  // Unlike Checkout there is no way to mark the return URL (it's Lago
  // provider-level config), so we leave the marker on this side instead:
  // snapshot the figures a successful payment has to move, and recognise the
  // change on the way back (#2359).
  const openPaymentAction = () => {
    if (!billing?.paymentActionUrl) return
    rememberPaymentReturn(orgId, {
      planTier: billing.planTier,
      pendingProUpgrade: billing.pendingProUpgrade,
      invoicePaymentStatus: billing.lastInvoice?.paymentStatus ?? null,
      startedAt: Date.now(),
    })
    // Lago mints this page from the same Stripe provider whose
    // `success_redirect_url` points at the org-agnostic
    // `/billing/checkout-return` route. If it redirects there, that route needs
    // a stashed org or it bounces the admin to the org list; stashing one costs
    // nothing on the paths that return some other way.
    rememberCheckoutReturnOrg(orgId)
    window.location.assign(billing.paymentActionUrl)
  }

  /* --------------------------- Stripe / payment return --------------------- */

  // Set when a Checkout return should continue into the Pro charge (#2196):
  // the card-add began from the Upgrade CTA and completed successfully. The
  // charge itself waits for the refreshed billing state (continuation below).
  //
  // The intent is consumed here, at mount, for the same reason: it belongs to
  // the Checkout return this load *is*. Consuming it whenever a marker is
  // present — success or cancel — stops a stale intent leaking into a later,
  // unrelated checkout, which is what the effect used to do.
  //
  // Read ahead of `returnStatus` below rather than after it, because it is what
  // tells an upgrade return from a card-only one (#2762) and that decides
  // whether the card-related banner may render at all.
  const [continueProUpgrade, setContinueProUpgrade] = useState(() => {
    if (!search.checkout) return false
    const hadUpgradeIntent = takeProUpgradeIntent(orgId)
    return search.checkout === 'success' && hadUpgradeIntent
  })

  // Captured at mount rather than in the effect below. A Checkout return is a
  // fresh document load carrying the marker, so mount is exactly when it is
  // known — the same read-and-clear shape `paymentReturn` uses above. Setting it
  // from an effect committed one render with the marker in the URL but no
  // banner, and put a setState in the effect body (a cascading render).
  //
  // A payment-action return can arrive carrying this same marker (both
  // redirects share Lago's provider-level `success_redirect_url`). The payment
  // banner is the accurate one in that case — "payment method submitted" is not
  // what happened — so let it own the slot and only strip the param below.
  //
  // Latched here but not necessarily *rendered*: on the upgrade path "Payment
  // method submitted. It may take a moment to be confirmed." is about the card,
  // not the Pro plan the admin just bought, and it was the first of the
  // half-completed signals that return path showed them (#2762). That
  // suppression is applied at the point of render rather than here, keyed on
  // `upgradeChargeInFlight`, so a return whose continuation then declines to
  // charge — already Pro, already pending, or a billing query that is erroring
  // — still acknowledges the card-add the admin did just complete. Deciding it
  // in this initializer instead would silently drop the acknowledgement on the
  // erroring branch, where nothing else on the page reports the new card.
  const [returnStatus, setReturnStatus] = useState<'success' | 'cancel' | null>(
    () => (search.checkout && !paymentReturn ? search.checkout : null),
  )

  // Whether this org had a card before the Checkout we are returning from
  // (#2668) — `null` when unknown. Read-and-clear at mount, the same shape
  // `paymentReturn` and `continueProUpgrade` use, and for the same reason: the
  // stash belongs to the redirect this load IS, and leaving it behind would let
  // it decide some later, unrelated checkout.
  const [hadPaymentMethodBefore] = useState(() =>
    search.checkout ? takeCheckoutHadPaymentMethod() : null,
  )

  // Retire the success banner once the billing state confirms the card, rather
  // than leaving it to a hand dismissal it may never get (#2668). Only on the
  // add-card path — see `isCheckoutReturnConfirmed` for why a replacement
  // cannot be confirmed from here at all. Decided during render: it is a pure
  // question about state already held, and the `paymentConfirmed` latch above
  // sets the precedent for answering it here instead of in an effect that would
  // cost a cascading render.
  if (
    returnStatus === 'success' &&
    isCheckoutReturnConfirmed(billing, hadPaymentMethodBefore)
  ) {
    setReturnStatus(null)
  }

  // On return from Stripe Checkout: refetch the (now-changed) billing state +
  // parent org, then strip the query param so a refresh doesn't re-trigger this.
  useEffect(() => {
    if (!search.checkout) return
    queryClient.invalidateQueries({ queryKey: ['org-billing', orgId] })
    router.invalidate()
    navigate({
      to: '/orgs/$orgId/billing',
      params: { orgId },
      search: {},
      replace: true,
    })
  }, [search.checkout, orgId, queryClient, navigate, router])

  // Watch the refetched billing state for the payment landing, and latch the
  // first sighting — the tier is known to flap for ~90s after an upgrade
  // (#2360), so "confirmed" must be a one-way door or the banner would flicker
  // in lockstep with that bug. On confirmation, re-run the parent org loader so
  // the cards that read the org row catch up with the banner.
  //
  // Latching is a pure decision about state we already hold, so it happens
  // during render; the effect below only performs the side effect it implies.
  if (
    paymentReturn &&
    !paymentConfirmed &&
    isPaymentConfirmed(paymentReturn, billing)
  ) {
    setPaymentConfirmed(true)
  }

  // Latched on a ref, not just on `paymentConfirmed`: that flag now stays true
  // for the rest of the page's life, so without this the loader would re-run on
  // any later change to `router`'s identity. The old effect got this for free
  // because `paymentConfirmed` was both the trigger and the early-out.
  const parentReloadedRef = useRef(false)
  useEffect(() => {
    if (!paymentConfirmed || parentReloadedRef.current) return
    parentReloadedRef.current = true
    router.invalidate()
  }, [paymentConfirmed, router])

  // Close the watch window on a wall clock rather than on refetches: a billing
  // query that is erroring never delivers one, and the banner must not sit on
  // "Confirming…" forever. Timed from the return, not from the redirect — the
  // admin may have spent minutes on the 3-D Secure challenge.
  useEffect(() => {
    if (!paymentReturn) return
    const timer = window.setTimeout(
      () => setPaymentTimedOut(true),
      PAYMENT_CONFIRM_TIMEOUT_MS,
    )
    return () => window.clearTimeout(timer)
  }, [paymentReturn])

  // Close the settlement watch on a wall clock as well as on the state (#2654)
  // — the same belt-and-braces the payment-return watch uses above, and for the
  // same reason: a billing query that is erroring never delivers a resolution,
  // and the page must not poll forever. Timed from the charge.
  //
  // Its own constant, deliberately NOT `PAYMENT_CONFIRM_TIMEOUT_MS`: that is
  // 90s, the same as the server's settling window, so reusing it would stop the
  // polling at the exact moment the "Complete payment" CTA becomes available.
  // See `UPGRADE_SETTLEMENT_WATCH_MS`.
  //
  // Cancelled on resolution, not just on unmount: otherwise the timer outlives
  // the watch it belongs to and fires on the happy path too, latching the flag
  // for the rest of the session.
  useEffect(() => {
    if (upgradeChargedAt === null || upgradeSettlementResolved) return
    const timer = window.setTimeout(
      () => setUpgradeWatchExpired(true),
      UPGRADE_SETTLEMENT_WATCH_MS,
    )
    return () => window.clearTimeout(timer)
  }, [upgradeChargedAt, upgradeSettlementResolved])

  // When the upgrade lands, the tier has moved — re-run the parent org loader
  // so the cards and breadcrumb that read the org row catch up with the plan
  // card, which reads the billing query directly. Latched on a ref for the same
  // reason `parentReloadedRef` is: the condition stays true afterwards, so
  // without it the loader would re-run on any later change to `router`.
  // Keyed on the charge it reloaded for, not a bare boolean: a session-wide
  // latch is spent by the first upgrade, so a second one that lands would never
  // refresh the org row behind it.
  //
  // Its completion is recorded, not just fired (#2762): the processing state
  // holds the page until the org row agrees with the plan card, and this
  // reload is what moves it. Waiting on the tier alone could wait forever —
  // since #2658 a loader that fails keeps its last good org rather than
  // erroring — so the settled promise is the bound. Keyed by charge for the
  // same reason the ref is: a second charge in the same page session must not
  // inherit the first one's verdict.
  const upgradeReloadedForRef = useRef<number | null>(null)
  const [upgradeReloadSettledFor, setUpgradeReloadSettledFor] = useState<
    number | null
  >(null)
  useEffect(() => {
    if (!upgradeSettlementResolved || upgradeChargedAt === null) return
    if (upgradeReloadedForRef.current === upgradeChargedAt) return
    upgradeReloadedForRef.current = upgradeChargedAt
    const chargedAt = upgradeChargedAt
    void router
      .invalidate()
      .finally(() => setUpgradeReloadSettledFor(chargedAt))
  }, [upgradeSettlementResolved, upgradeChargedAt, router])

  // Continue the Starter → Pro upgrade after a card was added via Checkout
  // (#2196). Waits for the invalidated billing state to settle, then fires the
  // charge exactly once — the successful Checkout return is the "card added"
  // signal (see shouldContinueProUpgrade / #2215); the org just can't already
  // be Pro or mid-upgrade.
  //
  // The wait ends the moment the invalidated billing query settles, which is a
  // pure question about state already in hand — so it is answered during render,
  // consuming the flag either way so we never loop, and recording the verdict.
  // The effect is then left with only the side effect it implies. `charge` goes
  // null → true at most once, so the mutation still fires exactly once.
  const [chargeAfterCheckout, setChargeAfterCheckout] = useState<
    boolean | null
  >(null)
  if (continueProUpgrade && !billingQuery.isFetching) {
    setContinueProUpgrade(false)
    setChargeAfterCheckout(shouldContinueProUpgrade(billing))
  }

  // Latched on a ref so the charge fires exactly once by construction. The old
  // effect consumed `continueProUpgrade` in the same pass, which closed its own
  // guard; `chargeAfterCheckout` instead stays true, so on its own it would
  // re-charge on any later change to `startProUpgrade`'s identity. React Query
  // does keep `mutate` stable, but "we charge the customer once" should not rest
  // on that.
  const proUpgradeChargedRef = useRef(false)
  useEffect(() => {
    if (!chargeAfterCheckout || proUpgradeChargedRef.current) return
    proUpgradeChargedRef.current = true
    startProUpgrade({ afterCheckout: true })
  }, [chargeAfterCheckout, startProUpgrade])

  /* ------------ Upgrade intent carried in the URL (#2663) ---------------- */

  // The frozen-org takeover sends the admin here with `?upgrade=start` after
  // they clicked "Upgrade to DroidMap Pro" there. Continue that click instead
  // of making them press an identically-labelled button again.
  //
  // Captured at mount and held in state rather than read from `search` at the
  // point of use, because the effect below strips the param — the same
  // read-and-latch shape `returnStatus` and `continueProUpgrade` use.
  const [autoStartUpgrade, setAutoStartUpgrade] = useState(
    () => search.upgrade === 'start',
  )

  // Strip `?upgrade=start` so a refresh — or a bookmarked/shared URL — can't
  // start a second upgrade over the one this load already began.
  //
  // Deliberately NOT folded into the `search.checkout` effect above, which also
  // calls `router.invalidate()`: nothing has changed server-side on a bare
  // `?upgrade=start` arrival, so there is nothing to refetch. Re-running the
  // `$orgId` loader for no gain is the whole objection — it used to be a sharp
  // one, since that loader turned any transient org-GET failure into
  // "Organization not found", but #2658 taught it to hold its last good org
  // across a failed revalidation, so the cost is now wasted work rather than a
  // false claim about the customer's data. Restated rather than deleted: the
  // conclusion is unchanged, and the next reader should not have to re-derive
  // it from a premise that has moved on.
  //
  // It stands down when a checkout marker is present, since that effect
  // navigates to an empty search and clears this param with it.
  useEffect(() => {
    if (!search.upgrade || search.checkout) return
    navigate({
      to: '/orgs/$orgId/billing',
      params: { orgId },
      search: {},
      replace: true,
    })
  }, [search.upgrade, search.checkout, orgId, navigate])

  // What that intent resolves to once the billing state is in hand: the same
  // branch the plan card's own CTA would take, or nothing.
  //
  // Answered during render for the reason `chargeAfterCheckout` is — it is a
  // pure question about state already held, and consuming the flag either way
  // means it can't loop. `isPending` covers the first render (no data yet) and
  // `isFetching` a refetch in flight, which matters on a client-side navigation
  // that lands on a cached billing state: the decision must be made against the
  // fresh read, not the snapshot from an earlier visit. A query that errors
  // leaves both false with `billing` undefined, so the verdict is `'none'` and
  // the card shows its own error and Retry rather than us guessing at a charge.
  //
  // Deciding against a cached snapshot instead would risk auto-charging over an
  // upgrade a fresher read shows as already pending — the #2355 double charge,
  // reached by reading old state. The wait stays bounded even while one of this
  // page's polls is running (a stale `paymentReturn` stash, or the settlement
  // watch): every fetch settles and notifies its observers, so each poll leaves
  // a committed render with `isFetching` false to decide in.
  //
  // Note the coupling: `autoStartUpgrade` also holds the plan card's CTA in its
  // loading state (`isUpgradePending` below) and this is the only place it
  // clears, so whatever gates the decision gates that spinner too. Do not add a
  // condition here that can stay false indefinitely — and note that the one
  // state which already can (an offline, *paused* fetch: `isPending` true,
  // `fetchStatus` idle, so neither half of this gate is satisfied) is handled
  // at the `isUpgradePending` end rather than here, because the right answer
  // there is to release the spinner, not to decide without a read.
  const [autoStartAction, setAutoStartAction] = useState<
    'charge' | 'checkout' | 'none' | null
  >(null)
  if (autoStartUpgrade && !billingQuery.isPending && !billingQuery.isFetching) {
    setAutoStartUpgrade(false)
    setAutoStartAction(proUpgradeAutoStart(billing))
  }

  // Ref-latched so the auto-start fires exactly once by construction — the same
  // standard `proUpgradeChargedRef` sets just above, and for the same reason:
  // the verdict stays true afterwards, so on its own it would re-fire on any
  // later change to either callback's identity, and "we charge the customer
  // once" must not rest on a memo holding.
  const autoStartedRef = useRef(false)
  useEffect(() => {
    if (!autoStartAction || autoStartAction === 'none') return
    if (autoStartedRef.current) return
    autoStartedRef.current = true
    if (autoStartAction === 'charge') {
      startProUpgrade({ afterCheckout: false })
    } else {
      startProUpgradeCheckout()
    }
  }, [autoStartAction, startProUpgrade, startProUpgradeCheckout])

  /* ------------- Upgrade-processing page state (#2762) ------------------- */

  // A Pro upgrade THIS page session raised is live, from the moment the page
  // knows the Checkout return carries an upgrade intent through to the charge
  // resolving. Four sources, because the window has four beginnings and they
  // are not the same instant: waiting on the invalidated billing query before
  // the continuation can decide (`continueProUpgrade`), that decision coming
  // back "charge" (`chargeAfterCheckout`), the mutation in flight — which is
  // also the whole of the card-on-file "Upgrade to Pro" click straight from
  // the plan card, whose half-completed window is identical — and the charge
  // having landed.
  //
  // `chargeAfterCheckout === false` is a decision NOT to charge (already Pro,
  // or already mid-upgrade), so it deliberately reads as not in flight and the
  // page renders normally.
  //
  // `isPaused` is excluded, not folded in: React Query *pauses* a mutation
  // started offline rather than failing it — `status` stays `'pending'`, so
  // `isPending` stays true with nothing in flight and nothing that will ever
  // resolve it. Counting that as live would replace the whole page with a
  // spinner and no controls on it until the network came back, over a charge
  // that had not been sent. This page already reasons about the same pause
  // semantics on the query side (`isUpgradePending` below).
  const upgradeChargeInFlight =
    continueProUpgrade ||
    chargeAfterCheckout === true ||
    (proUpgradeMutation.isPending && !proUpgradeMutation.isPaused) ||
    upgradeChargedAt !== null

  // Bound the pre-charge half of the window on a wall clock, the way #2654
  // bounds the post-charge half. `startProUpgrade` is one API call, but nothing
  // client-side times it out, so a request that hangs rather than fails would
  // otherwise hold a full-page spinner with no controls on it indefinitely —
  // the dead end #2222 removed, reached from the one direction the settlement
  // watch is armed too late to cover.
  //
  // Cancelled once the charge lands, at which point `UPGRADE_SETTLEMENT_WATCH_MS`
  // takes over; re-armed per attempt from the mutation's `onMutate`.
  useEffect(() => {
    if (!upgradeChargeInFlight || upgradeChargedAt !== null) return
    const timer = window.setTimeout(
      () => setChargeStartExpired(true),
      UPGRADE_CHARGE_START_WATCH_MS,
    )
    return () => window.clearTimeout(timer)
  }, [upgradeChargeInFlight, upgradeChargedAt])

  // Whether to show one processing state instead of the cards. The predicate —
  // including every way out of it — is `isProUpgradeProcessing`.
  const upgradeProcessing = isProUpgradeProcessing({
    chargeInFlight: upgradeChargeInFlight,
    chargedAt: upgradeChargedAt,
    settlementResolved: upgradeSettlementResolved,
    watchExpired: upgradeWatchExpired || chargeStartExpired,
    chargeFailed: proUpgradeMutation.isError,
    paymentActionUrl: billing?.paymentActionUrl ?? null,
    billingUpdatedAt: billingQuery.dataUpdatedAt,
    billingPlanTier: billing?.planTier ?? null,
    orgPlanTier: org.planTier,
    orgReloadSettled:
      upgradeChargedAt !== null && upgradeReloadSettledFor === upgradeChargedAt,
  })

  // Tell the `$orgId` layout, so a frozen org gets one processing surface
  // rather than this one under a notice that either predates the payment or
  // repeats it. A no-op for a non-frozen org, which renders no notice at all.
  useDeclareBillingTakeover(upgradeProcessing)

  // The one exit that leaves nothing new on screen to explain why the page came
  // back.
  //
  // Not suppressed for a frozen org on the grounds that the layout's own
  // in-progress notice says the same sentence off its own watch. It does not:
  // that notice is unmounted for the whole takeover, so it mounts *here*, at
  // the instant this fires, with a fresh `expired: false` and 180s still to
  // run. Gating on it would mean the exit this exists for — a frozen org whose
  // upgrade never settles, which is the main path #2762 describes — says
  // nothing at all, under a notice still promising the org "unlocks by itself
  // as soon as Pro is active". The two lines can eventually co-exist, three
  // minutes later and only if the upgrade is still stuck; saying it twice then
  // beats saying it never.
  const showUpgradeTakingLonger =
    upgradeChargedAt !== null &&
    upgradeWatchExpired &&
    !upgradeSettlementResolved

  if (upgradeProcessing) {
    return <BillingUpgradeProcessingScreen />
  }

  return (
    <div className="space-y-5">
      {showUpgradeTakingLonger && <BillingUpgradeTakingLongerNotice />}

      {paymentReturnStatus ? (
        <PaymentReturnBanner
          status={paymentReturnStatus}
          onRefresh={() => billingQuery.refetch()}
          onDismiss={() => setPaymentReturnDismissed(true)}
        />
      ) : (
        // Suppressed while an upgrade this session raised is live, and after it
        // has been (`upgradeChargedAt` stays set): on that path the admin came
        // here for a plan, and a card-related success banner is the first of
        // the half-completed signals #2762 removes. It still renders when the
        // continuation declined to charge — the card-add is then the only thing
        // that happened, and on an erroring billing query it is the only thing
        // on the page that would report it.
        returnStatus &&
        !upgradeChargeInFlight && (
          <CheckoutReturnBanner
            status={returnStatus}
            onDismiss={() => setReturnStatus(null)}
          />
        )
      )}

      {/* Plan */}
      {showsPlanCard && (
        <BillingPlanCard
          planTier={planTier}
          creditPlan={creditPlan && { displayName: creditPlan.displayName }}
          billingMode={billingMode}
          entitlements={
            billing?.entitlements ??
            BILLING_PLAN_CATALOGUE[org.planTier].entitlements
          }
          trialProgress={trialProgress}
          trialCredits={creditPlan ? null : trialCredits}
          hasPaymentMethod={billing?.hasPaymentMethod ?? false}
          pendingProUpgrade={billing?.pendingProUpgrade ?? false}
          // Same outstanding-payment URL the payment-method card offers: the
          // up-front upgrade charge is an ordinary Lago invoice, so a 3-D Secure
          // step-up on it is exactly what this settles (#2222).
          paymentActionUrl={billing?.paymentActionUrl ?? null}
          onCompletePayment={openPaymentAction}
          onCancelProUpgrade={() => cancelUpgradeMutation.mutate()}
          isCancelPending={cancelUpgradeMutation.isPending}
          cancelError={cancelUpgradeMutation.isError}
          cancelStatus={cancelUpgradeMutation.data?.status ?? null}
          isLoading={billingQuery.isLoading}
          isError={billingQuery.isError}
          // Keep the CTA in its loading state through the auto-continue window so
          // it never flashes back to an idle "Upgrade to Pro" between the Checkout
          // return and the charge firing (#2196). `autoStartUpgrade` is the same
          // window for the frozen takeover's one-click arrival (#2663): on a
          // client-side navigation onto a cached billing state the card renders
          // its idle CTA while the refetch the decision waits on is in flight.
          //
          // Paired with `isFetching` rather than used alone, so the spinner is
          // held only while we are actually waiting on the read the decision
          // needs. Offline, React Query *pauses* the fetch — `isPending` true,
          // `fetchStatus` idle — which satisfies neither half of the gate above,
          // so `autoStartUpgrade` never clears; on its own it would leave the CTA
          // disabled at "Starting…" with nothing in flight to release it.
          isUpgradePending={
            proUpgradeMutation.isPending ||
            continueProUpgrade ||
            (autoStartUpgrade && billingQuery.isFetching)
          }
          upgradeError={billingActionFailure(proUpgradeMutation.error)}
          onStartProUpgrade={() =>
            proUpgradeMutation.mutate({ afterCheckout: false })
          }
          onAddPaymentMethod={startProUpgradeCheckout}
          isAddingPaymentMethod={checkout.isPending}
          addPaymentMethodError={billingActionFailure(checkout.error)}
          onRetry={() => billingQuery.refetch()}
        />
      )}

      {pendingCreditPlanTier && <CreditPlanSettlementWatch orgId={orgId} />}
      {canChangeCreditPlan && (creditPlanTier || choosesFirstPlan) && (
        <BillingCreditPlanPicker
          currentTier={creditPlanTier ?? null}
          pendingTier={pendingCreditPlanTier}
          paymentActionUrl={billing?.paymentActionUrl ?? null}
          isCancellingPending={cancelPendingMutation.isPending}
          onCancelPending={() => cancelPendingMutation.mutate()}
          cancelPendingMessage={
            cancelPendingMutation.data?.status === 'payment_succeeded'
              ? 'Your payment has already gone through, so this can’t be cancelled. Your new plan is starting now.'
              : cancelPendingMutation.isError
                ? (billingActionFailure(cancelPendingMutation.error)?.message ??
                  'Couldn’t cancel that change. Please try again in a moment.')
                : null
          }
          conversionFailed={conversionFailed}
          onChangeCard={() => {
            forgetCreditPlanIntent(orgId)
            checkout.startCheckout()
          }}
          scheduledChange={
            creditUsageQuery.data?.usage?.scheduledChange ?? null
          }
          periodEndsAt={creditUsageQuery.data?.usage?.resetsAt ?? null}
          hasPaymentMethod={billing?.hasPaymentMethod ?? false}
          card={cardQuery.data?.card ?? null}
          isChanging={changePlanMutation.isPending}
          isOpeningCheckout={checkout.isPending}
          changeError={
            planChangeFailure ??
            (checkout.isError
              ? (billingActionFailure(checkout.error)?.message ??
                'Couldn’t open the card form. Please try again in a moment.')
              : null)
          }
          onChange={(tier) => {
            checkout.reset()
            if (billing?.hasPaymentMethod) {
              changePlanMutation.mutate({ tier, afterCheckout: false })
              return
            }
            rememberCreditPlanIntent(orgId, tier)
            checkout.startCheckout()
          }}
          isCancellingScheduled={cancelScheduledMutation.isPending}
          cancelError={cancelScheduledMutation.isError}
          onCancelScheduled={() => cancelScheduledMutation.mutate()}
        />
      )}

      {creditPlan && (
        <BillingCreditsCard
          usage={creditUsageQuery.data?.usage ?? null}
          isLoading={creditUsageQuery.isLoading}
          isError={creditUsageQuery.isError}
          onRetry={() => creditUsageQuery.refetch()}
        />
      )}

      {/* Payment Method — hidden on legacy Starter: it's a free trial with no
          charges, so there's nothing to collect a card for until the admin
          upgrades, at which point the Upgrade CTA drives checkout itself. A credit
          Trial sees it once it has a card, per `showsPaymentMethodCard`. */}
      {showsPaymentMethodCard({
        isStarter,
        creditPlanTier,
        hasPaymentMethod: billing?.hasPaymentMethod ?? false,
      }) && (
        <BillingPaymentMethodCard
          hasPaymentMethod={billing?.hasPaymentMethod ?? false}
          // Only prompt for a card on a plan that actually gets card-charged.
          // Enterprise and the unlisted tiers are funded without one, so their
          // steady state would otherwise read as a setup gap (#2359).
          // An overage plan with no card is held to its bundle (#3002), whatever
          // the row's ADR-0013 tier still says. A per-org bundle is exempt from
          // that hold, so its steady state without a card isn't a setup gap.
          requiresPaymentMethod={
            creditPlanTier && creditPlan
              ? // A Trial only reaches this card with a card to manage.
                creditPlanTier === 'trial' ||
                (creditPlan.atBundleLimit === 'overage' &&
                  !hasPerOrgBundle(creditPlanTier))
              : requiresSelfServePaymentMethod(planTier)
          }
          paymentState={billing?.paymentState ?? 'current'}
          // A Trial owes nothing, and its only invoices are $0 closers.
          lastInvoice={
            creditPlanTier === 'trial' ? null : (billing?.lastInvoice ?? null)
          }
          paymentActionUrl={billing?.paymentActionUrl ?? null}
          isLoading={billingQuery.isLoading}
          isError={billingQuery.isError}
          isActionPending={checkout.isPending}
          actionError={checkout.isError}
          onAction={() => {
            forgetCreditPlanIntent(orgId)
            checkout.startCheckout()
          }}
          onCompletePayment={openPaymentAction}
          onRetry={() => billingQuery.refetch()}
        />
      )}

      {/* Wallet — prepaid only; postpaid spend now lives on the budget card. */}
      {isPrepaid && creditPlan === null && !awaitingFirstPlan && (
        <BillingWalletCard
          planTier={planTier}
          creditBalanceCents={walletBalanceCents}
          balanceSyncedAt={org.balanceSyncedAt}
          creditsExhausted={org.creditsExhausted}
          creditsLow={org.creditsLow}
          rechargeRule={rechargeQuery.data?.rechargeRule ?? null}
          isRechargeLoading={rechargeQuery.isLoading}
          isRechargeError={rechargeQuery.isError}
          isActionPending={rechargeMutation.isPending}
          actionError={rechargeMutation.isError}
          onSaveRecharge={(rule) => rechargeMutation.mutate(rule)}
          topUp={walletTopUp}
          onTopUp={() => topUpMutation.mutate()}
        />
      )}

      {/* Budget — Starter is a grant-capped trial where a customer budget adds
          no value: force it off (no budget, grant as the plan limit) and make
          the card read-only, but keep it visible so the feature stays known. */}
      {/* On a credit plan this is the overage cap (ADR-0027 D5). A plan that stops
          at its bundle has no overage to cap, and Custom's gate never stops, so
          its enforce toggle would promise something that doesn't happen. */}
      {((creditPlan === null && !awaitingFirstPlan) ||
        creditPlan?.atBundleLimit === 'overage') && (
        <BillingBudgetCard
          monthlyBudgetCents={isStarter ? null : org.monthlyBudgetCents}
          budgetEnforce={org.budgetEnforce}
          budgetEditable={!isStarter}
          // On Starter, spend is the compute drawn from the one-time grant
          // (grant − balance) — the same figure as the plan card's Trial Credits.
          // It's cumulative over the trial, not a calendar month, so it's labelled
          // "Trial spend" (below).
          //
          // Both arms are **cents** (#2357). The Starter arm used to divide by
          // 100 while the non-Starter arm passed the row's cents straight
          // through, so a single prop arrived in two different units depending on
          // the tier — which is why the card could not be fixed from inside. The
          // old comment here ("the tracked whole-dollar `monthlySpendCurrent`")
          // records the misreading: the row stores cents (`schema.ts:55`).
          monthlySpendCurrent={
            isStarter ? (trialCredits?.usedCents ?? 0) : org.monthlySpendCurrent
          }
          spendLabel={isStarter ? 'Trial spend' : undefined}
          overage={
            creditPlan
              ? {
                  creditsWarningAt: org.creditsWarningAt ?? null,
                  creditsReachedAt: org.creditsReachedAt ?? null,
                  emailsCreditAlerts:
                    !!creditPlanTier && !hasPerOrgBundle(creditPlanTier),
                }
              : undefined
          }
          // Starter's effective cap is its one-time grant ($5) — a hard stop with
          // no overage — so show that as the plan limit instead of the org row's
          // unlimited sentinel.
          monthlySpendLimit={
            isStarter ? (starterGrantCents ?? 0) / 100 : org.monthlySpendLimit
          }
          budgetWarningAt={org.budgetWarningAt}
          budgetReachedAt={org.budgetReachedAt}
          isActionPending={budgetMutation.isPending}
          actionError={budgetMutation.isError}
          onSave={(input) => budgetMutation.mutate(input)}
        />
      )}

      {/* Storage */}
      <BillingStorageCard
        status={storageQuery.data ?? null}
        storageSummary={org.storageSummary}
        isLoading={storageQuery.isLoading}
        isError={storageQuery.isError}
        isActionPending={ceilingMutation.isPending}
        actionError={ceilingMutation.isError}
        onSaveCeiling={(bytes) => ceilingMutation.mutate(bytes)}
        onRetry={() => storageQuery.refetch()}
      />

      {/* Invoices — starter has none by definition (see isStarter above). */}
      <BillingInvoicesCard
        invoices={isStarter ? [] : (invoicesQuery.data ?? [])}
        isLoading={invoicesQuery.isLoading}
        isError={invoicesQuery.isError}
        onRetry={() => invoicesQuery.refetch()}
      />
    </div>
  )
}
